import { useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { History, Loader2, PlusCircle, Pencil, Trash2, RotateCcw, Flame, UserCog, ArrowUpDown, ArrowLeft } from "lucide-react";
import CockpitLayout from "@/components/admin/CockpitLayout";
import LoadMore from "@/components/admin/LoadMore";
import { useCursorList } from "@/lib/useCursorList";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, can } from "@/lib/perms";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", GREEN = "#1E7E45";

const ICONS = { create: PlusCircle, update: Pencil, status: ArrowUpDown, delete: Trash2, restore: RotateCcw, purge: Flame };
const COLOR = { create: GREEN, update: GOLD, status: GOLD, delete: "#D97706", restore: GREEN, purge: "#DC2626" };

function timeAgo(iso) {
  try { return new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Aktivitaet() {
  const { user } = useAuth();
  const canView = !!(user && can(user, "activity:view"));
  const params = useMemo(() => ({}), []);
  const { items: rows, loading, loadingMore, error, hasMore, loadMore } = useCursorList("/activity", "entries", params, { enabled: canView });

  if (user && !can(user, "activity:view")) return <Navigate to="/dashboard" replace />;

  return (
    <CockpitLayout active="sicherheit" title="Aktivitäts-Protokoll">
      <div className="fundio-page-stack" data-testid="gold-aktivitaet">
        <div>
          <Link to="/dashboard/sicherheit" className="mb-4 flex w-fit items-center gap-1.5 text-sm hover:text-fundio-ink" style={{ color: SLATE }}><ArrowLeft size={16} /> Sicherheit</Link>
          <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="aktivitaet-title">
            <History size={28} style={{ color: GOLD }} /> Aktivitäts-Protokoll
          </h1>
          <p className="mt-1 fundio-muted text-[14px]">Wer hat was wann gemacht – die Aktionen dieses Brockis.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : rows.length === 0 ? (
          <div className="fundio-panel fundio-state" style={{ borderStyle: "dashed" }}>
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "rgba(216,154,0,.10)" }}><History style={{ color: GOLD }} /></span>
            <strong className="mt-3 block text-[16px] font-bold" style={{ color: INK }}>Noch keine Einträge</strong>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>Aktionen im Cockpit erscheinen hier automatisch.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="activity-list">
            {rows.map((r) => {
              const Icon = ICONS[r.action] || (r.target_type === "user" || r.target_type === "member" ? UserCog : Pencil);
              return (
                <div key={r.id} className="fundio-panel flex items-start gap-3" style={{ padding: 14 }}>
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ background: "#F5F2EA", color: COLOR[r.action] || SLATE }}>
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm" style={{ color: INK }}>{r.summary}</p>
                    <p className="mt-0.5 text-xs" style={{ color: SLATE }}>
                      <span style={{ color: MUTED }}>{r.user_name}</span>
                      <span className="mx-1.5">·</span>{roleLabel(r.role)}
                      <span className="mx-1.5">·</span>{timeAgo(r.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <LoadMore hasMore={hasMore} loading={loadingMore} error={error} onLoad={loadMore} testid="activity-load-more" />
      </div>
    </CockpitLayout>
  );
}
